import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'reactstrap';
import dayjs from 'dayjs';

import { ContainerLoading, ErrorResponse } from '~components';
import { faFileLines } from '~components/icon';
import { Column, Row, TableView } from '~components/table-view';
import { genericCellRender, linkCellRender } from '~components/table-view/columnRenderers';

import { getErrorMessage } from '~libs/axiosWrapper';

import { useGetCompletedListQuery } from '~forms/actions/completedForms';

const STATUS_COLORS: Record<string, string> = {
  draft: 'warning',
  submitted: 'success',
};

const CompletedFormsList: React.FC = () => {
  const navigate = useNavigate();
  const { data: completedForms, isLoading, isError, error } = useGetCompletedListQuery();

  const openCompleted = useCallback((row: Row) => navigate(`/forms/completed/${row.id}`), [navigate]);

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'form_name',
        header: 'Form Name',
        type: 'text',
        deriveValue: (row) => row.template?.title ?? '—',
        renderReadOnly: linkCellRender(openCompleted),
      },
      {
        id: 'client',
        header: 'Client',
        type: 'text',
        deriveValue: (row) => row.client?.full_name ?? '—',
      },
      {
        id: 'version',
        header: 'Version',
        type: 'text',
        deriveValue: (row) => (row.version ? `v${row.version.version_number}` : '—'),
      },
      {
        id: 'completed_by',
        header: 'Completed By',
        type: 'text',
        deriveValue: (row) => row.createdby?.full_name ?? '—',
      },
      {
        id: 'created_at',
        header: 'Completed At',
        type: 'text',
        deriveValue: (row) =>
          row.created_at ? dayjs(row.created_at as string).format('DD/MM/YYYY HH:mm') : '—',
      },
      {
        id: 'status',
        header: 'Status',
        type: 'text',
        renderReadOnly: genericCellRender((value) => (
          <Badge color={STATUS_COLORS[value] ?? 'secondary'} pill className="text-capitalize">
            {value}
          </Badge>
        )),
      },
    ],
    [openCompleted],
  );

  if (isLoading) return <ContainerLoading loading />;
  if (isError) return <ErrorResponse message={getErrorMessage(error)} dataCy="completed-forms-error" />;

  return (
    <TableView
      id="forms-completed"
      data={(completedForms ?? []) as Row[]}
      columns={columns}
      readonly
      striped
      isLoading={isLoading}
      emptyIcon={faFileLines}
      emptyMessage="No completed forms yet."
    />
  );
};

export default CompletedFormsList;
